'use client';

/**
 * The slot's refresh clock (the owner's decision, 2026-09-30; the server
 * side is lib/ads/refresh.ts).
 *
 * THE CLOCK COUNTS ONLY VIEWABLE TIME. It runs while ALL of these hold, and
 * pauses (never resets) while any fails:
 *   - the document is visible and the window has focus;
 *   - the slot is active (the device's one live slot: not under a dialog,
 *     Discover, Stage or a sheet, node-frame.tsx and canvas-cover.ts) and
 *     at least its minimum width, and the ad is drawn and fits (`eligible`);
 *   - at least half of the ad intersects the viewport and the three-point
 *     hit test lands inside it (`adSeeable`, components/ads/ad-trackers.ts:
 *     the SAME judge the impression and viewable beacons use, reused rather
 *     than copied);
 *   - a person is active: input within the idle cooldown (the Ads tab's
 *     `ads.config.idleSec`, three minutes by default: visible, focused time
 *     counts as watched that long after the last input) that the PAGE ITSELF
 *     saw, never a message from an app (S-364; the owner, 2026-09-30:
 *     "should not we register activity on the portal side above the
 *     sandbox?"). Two sources, neither of which an app can fake:
 *       1. the page's own pointer, key, wheel and touch events, outside
 *          any app frame;
 *       2. the browser's USER ACTIVATION, read on every sample
 *          (`navigator.userActivation.isActive`). A real key press (not
 *          Esc), mouse press or finished tap inside ANY frame, cross-origin
 *          app frames included, activates this page too for a few seconds
 *          (the HTML standard's activation notification reaches every
 *          ancestor, whatever its origin), and only a trusted event
 *          (`isTrusted`) can: a script, an app's included, cannot set it.
 *     Scrolling alone inside an app is not an activation, so a person who
 *     only scrolls in an app for longer than the cooldown pauses the clock:
 *     it errs toward fewer serves, never more. A browser without
 *     `navigator.userActivation` (before late 2023) counts the page's own
 *     input only. Nothing an app posts (the focus ping, any frame) moves
 *     this mark: the focus ping still raises the app's window, and that
 *     is all it does.
 * When the clock reaches the interval the slot asks for its next ad
 * (`onDue('time')`). Sampled every 250 ms: a suspended timer cannot credit
 * its gap (the delta is capped at two samples).
 *
 * NAVIGATION. The slot becoming active again (the top window or the screen
 * changed), or the tab regaining visibility or focus, is a navigation: when
 * the current ad has been in view at least 45 s (`NAVIGATION_MIN_VIEW_MS`,
 * EthicalAds' own number), the slot asks for its next ad with
 * `onDue('navigation')`, which every source may fill. Below 45 s nothing
 * happens and the ad stays.
 *
 * NEVER SWAP UNDER THE POINTER. A due (timed or navigation) is deferred
 * while `held`: the pointer over the ad, focus inside it, or "Why this ad"
 * open. It fires on the first sample after the hold ends. Once fired, this
 * ad's clock is done: the next ad starts a fresh one (`adKey` changes).
 *
 * The person-active tracker is one set of passive, capturing listeners for
 * the page, shared by every slot.
 */

import { useEffect, useRef, type RefObject } from 'react';

import { AD_IDLE_LIMITS, NAVIGATION_MIN_VIEW_SEC } from '@/lib/ads/refresh';
import type { AdRefreshTrigger } from '@/lib/ads/types';

import { adSeeable, VIEWABLE_RATIO } from './ad-trackers';

/**
 * The idle cooldown when the server names none (an older pod during a roll):
 * no input for this long and the person is taken to be away, so the clock
 * pauses. Visible, focused time before it counts as watched (the owner,
 * 2026-09-30: "eye ball time also counts"). The live value is the Ads tab's
 * setting, `ads.config.idleSec`, sent with every serve.
 */
export const USER_IDLE_MS = AD_IDLE_LIMITS.defaultSec * 1000;
/** The viewable time an ad needs before a navigation rotates it. */
export const NAVIGATION_MIN_VIEW_MS = NAVIGATION_MIN_VIEW_SEC * 1000;
/** How often the clock samples. Coarser than the MRC's 100 ms for the viewable second: nothing here is a measurement. */
export const REFRESH_SAMPLE_MS = 250;
/** The swap's crossfade; instant under `prefers-reduced-motion`. */
export const CROSSFADE_MS = 200;

/** The page's own input events (outside app frames, which keep theirs). */
const INPUT_EVENTS = ['pointerdown', 'pointermove', 'keydown', 'wheel', 'touchstart'] as const;

let lastInputAt = Date.now();
let tracking = false;

function noteInput(): void {
  lastInputAt = Date.now();
}

/**
 * The browser's transient user activation for this page: true for a few
 * seconds after a real key press, mouse press or tap in the page or in any
 * frame inside it. Set only by trusted input, so no app can raise it.
 */
function browserSawInput(): boolean {
  if (typeof navigator === 'undefined') return false;
  const activation = (navigator as Navigator & { userActivation?: { isActive?: boolean } }).userActivation;
  return activation?.isActive === true;
}

/** Start the page-wide activity listeners once. */
export function ensureInputTracking(): void {
  if (tracking || typeof window === 'undefined') return;
  tracking = true;
  for (const name of INPUT_EVENTS) window.addEventListener(name, noteInput, { capture: true, passive: true });
}

/**
 * True when the person gave the page, or an app in it, input within `ms`.
 * Reads the browser's user activation on every call, so the clock's 250 ms
 * sample catches input inside an app frame while the activation lasts. The
 * page's own load counts as input.
 */
export function userActiveWithin(ms = USER_IDLE_MS, now = Date.now()): boolean {
  if (browserSawInput()) lastInputAt = now;
  return now - lastInputAt <= ms;
}

/** Test hook: the last input was `now`. The listeners stay (they are idempotent). */
export function _resetAdRefreshForTests(now = Date.now()): void {
  lastInputAt = now;
}

/** The person asked for less motion: swaps are instant. */
export function prefersReducedMotion(): boolean {
  try {
    return typeof window !== 'undefined' ? window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false : false;
  } catch {
    return false;
  }
}

/** Run `cb` on the next frame (a timer where there is no frame), and a way to cancel it. */
export function nextFrame(cb: () => void): () => void {
  if (typeof requestAnimationFrame === 'function') {
    const id = requestAnimationFrame(() => cb());
    return () => cancelAnimationFrame(id);
  }
  const id = setTimeout(cb, 0);
  return () => clearTimeout(id);
}

export interface AdRefreshClockOptions {
  /** The drawn ad (the chip, the picture, the advertiser and the headline), not the slot region around it. */
  elementRef: RefObject<HTMLElement | null>;
  /** The slot is active and at least its minimum width, the ad is drawn and fits, and it is not a preview. */
  eligible: boolean;
  /** The timed interval in ms; 0 (or less) is off. The navigation path runs either way. */
  intervalMs: number;
  /** The idle cooldown in ms (the server's `idleSec`); absent: `USER_IDLE_MS`. */
  idleMs?: number;
  /** The pointer is over the ad, focus is inside it, or "Why this ad" is open: no swap until it ends. */
  held: boolean;
  /** Changes with every served ad (its token): the clock starts over. Null: nothing to time. */
  adKey: string | null;
  /** Once per ad, when due and not held: the slot asks for its next ad. */
  onDue: (trigger: AdRefreshTrigger) => void;
}

/** The refresh clock of one drawn ad. */
export function useAdRefreshClock({ elementRef, eligible, intervalMs, idleMs, held, adKey, onDue }: AdRefreshClockOptions): void {
  const accruedRef = useRef(0);
  // A ref, so a new cooldown from the next serve never restarts this ad's clock.
  const idleMsRef = useRef(idleMs && idleMs > 0 ? idleMs : USER_IDLE_MS);
  idleMsRef.current = idleMs && idleMs > 0 ? idleMs : USER_IDLE_MS;
  const pendingRef = useRef<AdRefreshTrigger | null>(null);
  const firedRef = useRef(false);
  const heldRef = useRef(held);
  const onDueRef = useRef(onDue);
  const wasEligibleRef = useRef(false);
  heldRef.current = held;
  onDueRef.current = onDue;

  useEffect(() => {
    ensureInputTracking();
  }, []);

  // A new ad, a fresh clock.
  useEffect(() => {
    accruedRef.current = 0;
    pendingRef.current = null;
    firedRef.current = false;
  }, [adKey]);

  // A navigation: the ad has been in view long enough, and the person came
  // back to it (the slot became active again) or to the tab.
  const noteNavigation = () => {
    if (firedRef.current || !adKey) return;
    if (accruedRef.current >= NAVIGATION_MIN_VIEW_MS) pendingRef.current = 'navigation';
  };

  useEffect(() => {
    if (eligible && !wasEligibleRef.current) noteNavigation();
    wasEligibleRef.current = eligible;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eligible]);

  useEffect(() => {
    if (!adKey || typeof window === 'undefined') return;
    const onFocus = () => noteNavigation();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') noteNavigation();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adKey]);

  // The sampler: accrue while everything holds, fire a due when not held.
  useEffect(() => {
    if (!eligible || !adKey) return;
    const el = elementRef.current;
    if (!el) return;

    let ratio = 0;
    let rendered = false;
    let io: IntersectionObserver | null = null;
    if (typeof IntersectionObserver !== 'undefined') {
      io = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) ratio = entry.isIntersecting ? entry.intersectionRatio : 0;
          rendered = true;
        },
        { threshold: [0, VIEWABLE_RATIO, 1] },
      );
      io.observe(el);
    }

    let last = Date.now();
    const timer = setInterval(() => {
      const now = Date.now();
      const delta = Math.min(now - last, REFRESH_SAMPLE_MS * 2);
      last = now;
      if (firedRef.current) return;
      const seen = rendered && ratio >= VIEWABLE_RATIO && adSeeable(el) && userActiveWithin(idleMsRef.current, now);
      if (seen) accruedRef.current += delta;
      if (intervalMs > 0 && accruedRef.current >= intervalMs && pendingRef.current === null) pendingRef.current = 'time';
      const due = pendingRef.current;
      if (due && !heldRef.current) {
        firedRef.current = true;
        pendingRef.current = null;
        onDueRef.current(due);
      }
    }, REFRESH_SAMPLE_MS);

    return () => {
      clearInterval(timer);
      io?.disconnect();
    };
  }, [eligible, adKey, intervalMs, elementRef]);
}

/** Test hook: the clock's accrued viewable time is private; tests read outcomes (`onDue`) instead. */
export const _adRefreshInternals = { USER_IDLE_MS, NAVIGATION_MIN_VIEW_MS, REFRESH_SAMPLE_MS } as const;
